import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllInwards,
  fetchChallanAccessoryInwardAnalytics,
  fetchGRNDetail,
  fetchGRNListForChallan,
  fetchInwardFormData,
  fetchPendingChallans,
  processAccessoryInward,
} from "./thunk";

interface ReturnedAccessoriesInwardState {
  analytics: any;
  analyticsLoading: boolean;

  pendingChallans: any[];
  pendingChallansCount: number;
  pendingChallansLoading: boolean;

  inwardFormData: any;
  inwardFormLoading: boolean;

  processLoading: boolean;

  allInwards: any[];
  allInwardsCount: number;
  allInwardsLoading: boolean;

  grnList: any[];
  grnListLoading: boolean;

  grnDetail: any;
  grnDetailLoading: boolean;

  error: string | null;
}

const initialState: ReturnedAccessoriesInwardState = {
  analytics: null,
  analyticsLoading: false,

  pendingChallans: [],
  pendingChallansCount: 0,
  pendingChallansLoading: false,

  inwardFormData: null,
  inwardFormLoading: false,

  processLoading: false,

  allInwards: [],
  allInwardsCount: 0,
  allInwardsLoading: false,

  grnList: [],
  grnListLoading: false,

  grnDetail: null,
  grnDetailLoading: false,

  error: null,
};

const returnedAccessoriesInwardSlice = createSlice({
  name: "returnedAccessoriesInward",
  initialState,
  reducers: {
    clearInwardFormData(state) {
      state.inwardFormData = null;
    },
    clearGRNDetail(state) {
      state.grnDetail = null;
    },
    clearGRNList(state) {
      state.grnList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChallanAccessoryInwardAnalytics.pending, (state) => {
        state.analyticsLoading = true;
      })
      .addCase(fetchChallanAccessoryInwardAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload?.data ?? action.payload;
      })
      .addCase(fetchChallanAccessoryInwardAnalytics.rejected, (state) => {
        state.analyticsLoading = false;
      })

      .addCase(fetchPendingChallans.pending, (state) => {
        state.pendingChallansLoading = true;
      })
      .addCase(fetchPendingChallans.fulfilled, (state, action) => {
        state.pendingChallansLoading = false;
        state.pendingChallans = action.payload?.data ?? action.payload ?? [];
        state.pendingChallansCount = action.payload?.count ?? action.payload?.totalRecords ?? state.pendingChallans.length;
      })
      .addCase(fetchPendingChallans.rejected, (state) => {
        state.pendingChallansLoading = false;
      })

      .addCase(fetchInwardFormData.pending, (state) => {
        state.inwardFormLoading = true;
        state.inwardFormData = null;
      })
      .addCase(fetchInwardFormData.fulfilled, (state, action) => {
        state.inwardFormLoading = false;
        state.inwardFormData = action.payload?.data ?? action.payload;
      })
      .addCase(fetchInwardFormData.rejected, (state) => {
        state.inwardFormLoading = false;
      })

      .addCase(processAccessoryInward.pending, (state) => {
        state.processLoading = true;
      })
      .addCase(processAccessoryInward.fulfilled, (state) => {
        state.processLoading = false;
      })
      .addCase(processAccessoryInward.rejected, (state) => {
        state.processLoading = false;
      })

      .addCase(fetchAllInwards.pending, (state) => {
        state.allInwardsLoading = true;
      })
      .addCase(fetchAllInwards.fulfilled, (state, action) => {
        state.allInwardsLoading = false;
        state.allInwards = action.payload?.data ?? action.payload ?? [];
        state.allInwardsCount = action.payload?.count ?? action.payload?.totalRecords ?? state.allInwards.length;
      })
      .addCase(fetchAllInwards.rejected, (state) => {
        state.allInwardsLoading = false;
      })

      .addCase(fetchGRNListForChallan.pending, (state) => {
        state.grnListLoading = true;
      })
      .addCase(fetchGRNListForChallan.fulfilled, (state, action) => {
        state.grnListLoading = false;
        state.grnList = action.payload?.data ?? action.payload ?? [];
      })
      .addCase(fetchGRNListForChallan.rejected, (state) => {
        state.grnListLoading = false;
      })

      .addCase(fetchGRNDetail.pending, (state) => {
        state.grnDetailLoading = true;
      })
      .addCase(fetchGRNDetail.fulfilled, (state, action) => {
        state.grnDetailLoading = false;
        state.grnDetail = action.payload?.data ?? action.payload;
      })
      .addCase(fetchGRNDetail.rejected, (state) => {
        state.grnDetailLoading = false;
      });
  },
});

export const { clearInwardFormData, clearGRNDetail, clearGRNList } =
  returnedAccessoriesInwardSlice.actions;

export default returnedAccessoriesInwardSlice.reducer;
